import React, { useCallback, useMemo, useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import StreakIcon from "../../assets/gif/streak.gif";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import dayjs from "dayjs";

const weekdayLabels = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];

const ActivityConsistencyCard = ({ currentStreak = [], showTitle = true }) => {
  const computeInitialDate = useCallback(() => {
    if (
      !currentStreak?.streak_dates ||
      currentStreak?.streak_dates.length === 0
    ) {
      return new Date();
    }
    let filtereddates = currentStreak?.streak_dates;

    const sortedDates = [...filtereddates].sort((a, b) => (a > b ? 1 : -1));
    const latestDate = sortedDates[sortedDates.length - 1];
    const [year, month] = latestDate.split("-");
    return new Date(Number(year), Number(month) - 1, 1);
  }, [currentStreak]);

  const [currentDate, setCurrentDate] = useState(() => computeInitialDate());

  useEffect(() => {
    setCurrentDate(computeInitialDate());
  }, [computeInitialDate]);

  const [showYearDropdown, setShowYearDropdown] = useState(false);

  const handleYearChange = (newDate) => {
    if (newDate) {
      const year = newDate.year();
      const updatedDate = new Date(currentDate);
      updatedDate.setFullYear(year);
      setCurrentDate(updatedDate);
      setShowYearDropdown(false);
    }
  };

  const activeDatesSet = useMemo(
    () => new Set(currentStreak?.streak_dates || []),
    [currentStreak],
  );

  const { year, month, days } = useMemo(() => {
    const dateYear = currentDate.getFullYear();
    const dateMonth = currentDate.getMonth();
    const firstDay = new Date(dateYear, dateMonth, 1).getDay();
    const daysInMonth = new Date(dateYear, dateMonth + 1, 0).getDate();

    const calendarDays = [];
    const prevMonthLastDay = new Date(dateYear, dateMonth, 0).getDate();
    const startOffset = firstDay;

    for (let i = startOffset - 1; i >= 0; i--) {
      const day = prevMonthLastDay - i;
      calendarDays.push({ day, current: false, monthOffset: -1 });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      calendarDays.push({ day, current: true, monthOffset: 0 });
    }

    const remaining = 42 - calendarDays.length;
    for (let day = 1; day <= remaining; day++) {
      calendarDays.push({ day, current: false, monthOffset: 1 });
    }

    return { year: dateYear, month: dateMonth, days: calendarDays };
  }, [currentDate]);

  return (
    <div className="card-styled-wrapper activity-consistency-card">
      <div className="activity-consistency-card-content">
        {showTitle && (
          <h3 className="activity-consistency-card-title">
            Activity Consistency
          </h3>
        )}

        <div className="activity-consistency-banner">
          <div className="activity-consistency-banner-left">
            <img
              src={StreakIcon}
              alt="Active streak"
              className="activity-consistency-banner-image"
            />
            <span className="activity-consistency-banner-label">
              Active streak
            </span>
          </div>
          <div className="activity-consistency-banner-right">
            <span className="activity-consistency-banner-value">
              {currentStreak?.active_streak_count || 0}
            </span>
            <span className="activity-consistency-banner-unit">days</span>
          </div>
        </div>
        <div className="activity-consistency-calendar">
          <div className="activity-consistency-calendar-header">
            <div className="activity-consistency-calendar-header-left">
              <span className="activity-consistency-calendar-month">
                {currentDate.toLocaleString("default", {
                  month: "long",
                  year: "numeric",
                })}
              </span>
              <button
                type="button"
                className="activity-consistency-calendar-icon-button"
                onClick={() => setShowYearDropdown(!showYearDropdown)}
              >
                <ChevronRight
                  className="activity-consistency-calendar-icon"
                  style={{
                    transform: showYearDropdown
                      ? "rotate(-90deg)"
                      : "rotate(90deg)",
                    transition: "transform 0.2s ease",
                  }}
                />
              </button>
              {showYearDropdown && (
                <div className="activity-consistency-year-dropdown">
                  <div onClick={(e) => e.stopPropagation()}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DateCalendar
                        views={["year"]}
                        openTo="year"
                        value={dayjs(currentDate)}
                        onChange={handleYearChange}
                      />
                    </LocalizationProvider>
                  </div>
                </div>
              )}
            </div>
            <div className="activity-consistency-calendar-nav">
              <button
                type="button"
                onClick={() => setCurrentDate(new Date(year, month - 1, 1))}
                className="activity-consistency-calendar-nav-button"
              >
                <ChevronLeft />
              </button>
              <button
                type="button"
                onClick={() => setCurrentDate(new Date(year, month + 1, 1))}
                className="activity-consistency-calendar-nav-button"
              >
                <ChevronRight />
              </button>
            </div>
          </div>
          <div className="activity-consistency-weekdays">
            {weekdayLabels.map((label) => (
              <span key={label} className="activity-consistency-weekday">
                {label?.toUpperCase()}
              </span>
            ))}
          </div>
          <div className="activity-consistency-grid">
            {days.map((dayInfo, index) => {
              const dateObj = new Date(
                year,
                month + dayInfo.monthOffset,
                dayInfo.day,
              );
              const dateStr = `${dateObj.getFullYear()}-${String(
                dateObj.getMonth() + 1,
              ).padStart(2, "0")}-${String(dateObj.getDate()).padStart(
                2,
                "0",
              )}`;

              const isActive = dayInfo.current && activeDatesSet.has(dateStr);

              const dayClassNames = [
                "activity-consistency-day-inner",
                isActive
                  ? "activity-consistency-day-inner--active"
                  : dayInfo.current
                    ? "activity-consistency-day-inner--current"
                    : "activity-consistency-day-inner--muted",
              ].join(" ");

              return (
                <div key={index} className="activity-consistency-day">
                  <div className={dayClassNames}>{dayInfo.day}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
export default ActivityConsistencyCard;
